import React from "react";
import "./Button.css";
import { clsx } from "../../utils/clsx";
import { AntdNettgravSpinner as DefaultLoadingIcon } from "../Spinner/Spinners";
import { useComponentDefaults } from "../../Provider";

/*
  Enhanced Button API (backward compatible)
  - Supports AntD-like props: type, variant, color, danger, ghost, block
  - Size: small|middle|large (maps to sm|md|lg)
  - Shape: default|round|circle
  - Icons: icon, iconPosition (start|end), leftIcon/rightIcon
  - Loading: boolean | { delay?: number, icon?: ReactNode }
  - Link behavior: as/to (React Router) or href + target
  - htmlType: submit|reset|button
  - classNames/styles: { root, icon, content }
  - autoInsertSpace: boolean (Chinese two-char spacing)
*/

export default function Button(props) {
  // Merge props with DS defaults (agent.me → roles.theme/components)
  const merged = useComponentDefaults('Button', props, {
    variant: 'outlined',
    color: 'default',
    size: 'md',
    shape: 'default',
  });

  const {
    // Style intent
    type, // primary | link | text | default (sugar)
    variant: variantProp = "outlined", // legacy variants or new: filled|outlined|solid|dashed|text|link
    color: colorProp = "default", // default|primary|danger|preset or CSS var
    danger = false,
    ghost = false,
    block = false,

    // Size/shape
    size: sizeProp = "md", // large|middle|small or sm|md|lg
    shape = "default",

    // Icons
    icon,
    iconPosition = "start",
    leftIcon,
    rightIcon,

    // Loading
    loading: loadingProp = false,

    // Link & native attributes
    as: CompProp,
    to,
    href,
    target,
    rel,
    htmlType,
    disabled,
    onClick,

    // Class & style mapping
    className,
    classNames,
    styles,
    style: inlineStyle,

    // Content
    children,
    autoInsertSpace = true,
    ...rest
  } = merged;

  const htmlButtonTypes = new Set(["button", "submit", "reset"]);
  const nativeTypeFromTypeProp =
    typeof type === "string" && htmlButtonTypes.has(type.toLowerCase()) ? type.toLowerCase() : null;

  // Map legacy variant values to new system (filled + color)
  const mapLegacy = (v) => {
    switch (v) {
      case "primary":
        return { variant: "filled", color: "primary" };
      case "secondary":
        return { variant: "outlined", color: "default" };
      case "subtle":
        return { variant: "text", color: "default" };
      case "destructive":
        return { variant: "filled", color: "danger" };
      case "link":
        return { variant: "link", color: "primary" };
      default:
        return { variant: v, color: colorProp };
    }
  };

  const mapType = (t) => {
    switch (t) {
      case "primary":
        return { variant: "filled", color: "primary" };
      case "link":
        return { variant: "link", color: "primary" };
      case "text":
        return { variant: "text", color: "primary" };
      case "default":
      default:
        return { variant: "outlined", color: "default" };
    }
  };

  const legacy = mapLegacy(variantProp);
  const fromType = nativeTypeFromTypeProp ? null : type ? mapType(type) : null;
  const variant = fromType?.variant || legacy.variant || "filled";
  let color = fromType?.color || legacy.color || colorProp || "default";
  if (danger && (!colorProp || colorProp === "default")) color = "danger";

  // Normalize size
  const size = (() => {
    switch (String(sizeProp)) {
      case "small":
      case "sm":
        return "sm";
      case "large":
      case "lg":
        return "lg";
      case "middle":
      case "md":
      default:
        return "md";
    }
  })();

  // Loading handling: boolean or { delay, icon }
  const [delayedLoading, setDelayedLoading] = React.useState(false);
  const isLoading = typeof loadingProp === "boolean" ? loadingProp : delayedLoading;
  const loadingIcon =
    typeof loadingProp === "object" && loadingProp?.icon ? (
      loadingProp.icon
    ) : (
      <DefaultLoadingIcon size={16} />
    );
  React.useEffect(() => {
    if (typeof loadingProp === "object" && loadingProp?.delay) {
      const t = setTimeout(() => setDelayedLoading(true), loadingProp.delay);
      return () => clearTimeout(t);
    } else {
      setDelayedLoading(false);
    }
  }, [loadingProp]);

  // Icon resolution: `icon` with position or explicit left/rightIcon
  const resolvedLeftIcon = icon ? (iconPosition === "start" ? icon : null) : leftIcon;
  const resolvedRightIcon = icon ? (iconPosition === "end" ? icon : null) : rightIcon;

  // Auto insert space for two Chinese characters
  const renderChildren = () => {
    if (!children) return children;
    if (!autoInsertSpace || typeof children !== "string") return children;
    const twoCJK = /^[\u4e00-\u9fff]{2}$/; // basic CJK range
    if (twoCJK.test(children)) {
      return children.split("").join(" ");
    }
    return children;
  };

  // Icon-only heuristics: if no children and an icon is provided, treat as icon-only
  const hasChildren = React.Children.count(children) > 0 && !(typeof children === 'string' && children.trim() === '');
  const isIconOnly = !hasChildren && (resolvedLeftIcon || resolvedRightIcon);

  // If icon-only and no explicit non-default shape provided, prefer circle
  const computedShape = isIconOnly && shape === 'default' ? 'circle' : shape;

  const Comp = CompProp || (href ? "a" : "button");
  const nativeType = htmlType || nativeTypeFromTypeProp || (href || CompProp ? undefined : "button");
  const linkRel = rel || (target === "_blank" ? "noopener noreferrer" : undefined);
  const linkProps = CompProp
    ? to != null
      ? { to, target, rel: linkRel }
      : {}
    : href
    ? {
        href,
        target,
        rel: linkRel,
      }
    : {};

  // Allow custom color value via `color` prop (e.g., '#123456' or 'var(--gray-6)')
  const presetColors = new Set(["default","primary","danger","rose","emerald","amber","info"]);
  const rootStyle = {
    ...styles?.root,
    ...(color && typeof color === 'string' && !presetColors.has(color)
      ? { '--btn-color': color }
      : null),
    ...(inlineStyle || {}),
  };

  return (
    <Comp
      className={clsx(
        "ds-btn",
        // New variant/color system
        variant && `ds-btn--variant-${variant}`,
        color && `ds-btn--color-${color}`,
        // Back-compat legacy classes (keep existing styles working)
        variantProp && `ds-btn--${variantProp}`,
        // Size/shape
        `ds-btn--${size}`,
        `ds-btn--shape-${computedShape}`,
        isIconOnly && "ds-btn--icon-only",
        // Modifiers
        ghost && "ds-btn--ghost",
        block && "ds-btn--block",
        isLoading && "is-loading",
        className,
        classNames?.root
      )}
      style={rootStyle}
      aria-busy={isLoading || undefined}
      disabled={Comp === "button" ? disabled : undefined}
      onClick={onClick}
      type={Comp === "button" ? nativeType : undefined}
      {...linkProps}
      {...rest}
    >
      {isLoading ? (
        <span className={clsx("ds-btn__icon", classNames?.icon)} style={styles?.icon} aria-hidden>
          {loadingIcon}
        </span>
      ) : null}
      {resolvedLeftIcon && !isLoading ? (
        <span className={clsx("ds-btn__icon", classNames?.icon)} style={styles?.icon} aria-hidden>
          {resolvedLeftIcon}
        </span>
      ) : null}
      {hasChildren ? (
        <span className={clsx(classNames?.content)} style={styles?.content}>
          {renderChildren()}
        </span>
      ) : null}
      {resolvedRightIcon && !isLoading ? (
        <span className={clsx("ds-btn__icon", classNames?.icon)} style={styles?.icon} aria-hidden>
          {resolvedRightIcon}
        </span>
      ) : null}
    </Comp>
  );
}
